import { useEffect, useState } from 'react';
import { Combobox } from '@headlessui/react';
import { Label, Message, Spinner } from '@iwoca/orion';
import { t, Trans } from '@lingui/macro';
import { ButtonLink } from 'components/ButtonLink/ButtonLink';
import { SearchableDropdown } from 'components/SearchableDropdown/SearchableDropdown';
import { CriteriaItem } from 'components/Signup/NewSignupForm/CriteriaItem/CriteriaItem';
import { useAmplitude } from 'hooks/useAmplitude';
import { TCompanySearchResultDE } from 'lendingApi/lendingApi.types';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import {
  getCompanies,
  getLegalForm,
  useCompanyInput,
} from './CompanyInputDE.helpers';
import styles from './CompanyInputDE.module.css';
import { ManualCompanyInputDE } from './ManualCompanyInputDE/ManualCompanyInputDE';

export const CompanyInputDE = () => {
  const { trackEvent } = useAmplitude();
  const {
    isManualInput,
    toggleManualInput,
    denormalisedSelectedCompany,
    resetCompanyDetails,
    updateCompanyDetailsFromLookup,
    lookupError,
  } = useCompanyInput();

  const [hasLegalForm, setHasLegalForm] = useState(
    Boolean(denormalisedSelectedCompany?.legalForm),
  );
  const [isFetchingLegalForm, setIsFetchingLegalForm] = useState(false);

  const [selectedCompany, setSelectedCompany] = useState<
    TCompanySearchResultDE | undefined
  >(denormalisedSelectedCompany);

  useEffect(() => {
    if (!isManualInput) {
      setSelectedCompany(denormalisedSelectedCompany);
      setHasLegalForm(Boolean(denormalisedSelectedCompany?.legalForm));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isManualInput, denormalisedSelectedCompany?.name]);

  const handleChange = async (value: TCompanySearchResultDE) => {
    setIsFetchingLegalForm(true);
    const legalForm = await getLegalForm(value.crefo_id);
    setSelectedCompany(value);
    setHasLegalForm(Boolean(legalForm));
    updateCompanyDetailsFromLookup(value, legalForm);
    trackEvent(AMPLITUDE_EVENTS.companySearchSelected, {
      'Company name': value.name,
    });
    setIsFetchingLegalForm(false);
  };

  const handleClear = () => {
    setSelectedCompany(undefined);
    setHasLegalForm(false);
    resetCompanyDetails();
  };

  const handleToggle = () => {
    toggleManualInput();
    trackEvent(AMPLITUDE_EVENTS.companyInputToggleClicked, {
      'CTA text': isManualInput ? 'Search your company' : 'Enter manually',
    });
  };

  const renderLabel = () => (
    <div className={styles.label}>
      {isManualInput ? (
        <Label htmlFor="companyInputGermany">
          <Trans>Business name</Trans>
        </Label>
      ) : (
        <Combobox.Label>
          <Label>
            <Trans>Business name</Trans>
          </Label>
        </Combobox.Label>
      )}

      <ButtonLink
        data-testid="input-mode-toggle"
        onClick={handleToggle}
        type="button"
      >
        {isManualInput ? t`Search your company` : t`Enter manually`}
      </ButtonLink>
    </div>
  );

  if (isManualInput) {
    return <ManualCompanyInputDE labelComponent={renderLabel()} />;
  }

  return (
    <div data-testid="crefo-search">
      <SearchableDropdown
        labelComponent={renderLabel()}
        searchQuery={selectedCompany?.name}
        onChange={handleChange}
        optionsGetter={(searchQuery) => getCompanies(searchQuery)}
        optionRenderer={(company) => `${company.name} (${company.city})`}
        valueRenderer={(company) => company.name}
        placeholder={t`Search...`}
        onClear={handleClear}
      />
      {hasLegalForm && (
        <CriteriaItem hasPassed className={styles.confirmation}>
          <Trans>
            We were able to copy the founding date and legal form from the
            Crefo.
          </Trans>
        </CriteriaItem>
      )}
      {isFetchingLegalForm && <Spinner className={styles.spinner} />}

      {lookupError && (
        <Message
          className={styles.lookupError}
          message={lookupError}
          type="error"
        />
      )}
      {selectedCompany && !hasLegalForm && (
        <CriteriaItem className={styles.confirmation}>
          <Trans>
            Unable to retrieve company information on Crefo, please use manual
            input.
          </Trans>
        </CriteriaItem>
      )}
    </div>
  );
};
